import { ComponentProps, forwardRef, JSX, JSXElementConstructor, Ref } from 'react';
import clsx from 'clsx';

import styles from './Button.module.css';

type BaseButton = Pick<ComponentProps<'button'>, 'className' | 'children' | 'disabled' | 'type' | 'onClick' | 'style'> &
  Pick<ComponentProps<'a'>, 'href' | 'target'>;

interface ButtonProps extends BaseButton {
  variant?: 'primary' | 'ghost' | 'link' | 'nav';
  size?: 'xs' | 'sm' | 'lg' | 'xl';
  borderRadius?: 'lg' | '2xl';
  as?: keyof JSX.IntrinsicElements | JSXElementConstructor<any>;
  highlight?: boolean;
  invert?: boolean;
  suppressHydrationWarning?: boolean;
  itemProp?: string;
}

const borderRadiusMap = {
  lg: styles.borderRadiusLg,
  '2xl': styles.borderRadius2xl,
} as const;

function Button(
  {
    variant = 'primary',
    size = 'sm',
    borderRadius = '2xl',
    className,
    children,
    disabled,
    href,
    type = 'button',
    as: Tag = href ? 'a' : 'button',
    highlight,
    invert,
    ...props
  }: ButtonProps,
  ref: Ref<HTMLAnchorElement>,
) {
  return (
    <Tag
      ref={ref}
      className={clsx(
        styles.button,
        styles[size],
        styles[variant],
        borderRadiusMap[borderRadius],
        disabled && styles.disabled,
        highlight && styles.highlight,
        invert && styles.invert,
        className,
      )}
      disabled={disabled}
      type={type}
      href={href}
      {...props}
    >
      {children} {variant === 'link' ? ' →' : ''}
    </Tag>
  );
}

export default forwardRef(Button);
